import { useState, type FormEvent } from "react";
import type { Role, User } from "../api/types";
import { ROLES } from "../api/types";
import {
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
  useUsers,
} from "../hooks/queries";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { Input, Select } from "../components/ui/Input";
import { TableSkeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { errorMessage } from "../api/errors";
import { formatDateTime, formatRelative } from "../lib/format";

const roleOptions = ROLES.map((role) => ({
  value: role,
  label:
    role === "admin" ? "Yönetici" : role === "operator" ? "Operatör" : "Görüntüleyici",
}));

interface CreateUserForm {
  email: string;
  displayName: string;
  password: string;
  role: Role;
}

const emptyForm: CreateUserForm = {
  email: "",
  displayName: "",
  password: "",
  role: "viewer",
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const usersQuery = useUsers();
  const updateMutation = useUpdateUser();
  const createMutation = useCreateUser();
  const deleteMutation = useDeleteUser();

  const [form, setForm] = useState<CreateUserForm | null>(null);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  function onCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form) return;
    if (form.password.length < 12) {
      setFormError("Parola en az 12 karakter olmalı.");
      return;
    }
    setFormError(null);
    createMutation.mutate(
      {
        email: form.email,
        password: form.password,
        displayName: form.displayName || undefined,
        role: form.role,
      },
      {
        onSuccess: () => {
          toast("Kullanıcı oluşturuldu.", "success");
          setForm(null);
        },
        onError: (error) => setFormError(errorMessage(error)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Kullanıcılar</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Panel hesapları ve roller (yalnızca yönetici).
          </p>
        </div>
        <Button size="sm" onClick={() => setForm({ ...emptyForm })}>
          Yeni kullanıcı
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Hesaplar</CardTitle>
          <CardDescription>
            Rol değişiklikleri anında uygulanır; RBAC matrisi §9'da tanımlıdır.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {usersQuery.isPending ? (
            <TableSkeleton rows={3} columns={6} />
          ) : usersQuery.isError ? (
            <ErrorState
              message="Kullanıcı listesi yüklenemedi."
              onRetry={() => void usersQuery.refetch()}
            />
          ) : (usersQuery.data?.length ?? 0) === 0 ? (
            <EmptyState title="Kullanıcı yok" description="Kayıtlı panel hesabı bulunmuyor." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>E-posta</TH>
                  <TH>Ad</TH>
                  <TH>Rol</TH>
                  <TH>MFA</TH>
                  <TH>Oluşturma</TH>
                  <TH>Son giriş</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {usersQuery.data?.map((user) => (
                  <TR key={user.id}>
                    <TD>
                      <span className="font-medium">{user.email}</span>
                      {user.id === currentUser?.id ? (
                        <Badge tone="info" className="ml-2">
                          siz
                        </Badge>
                      ) : null}
                    </TD>
                    <TD>{user.displayName ?? "—"}</TD>
                    <TD>
                      <label className="sr-only" htmlFor={`role-${user.id}`}>
                        {user.email} rolü
                      </label>
                      <select
                        id={`role-${user.id}`}
                        value={user.role}
                        disabled={updateMutation.isPending}
                        onChange={(event) => {
                          const role = event.target.value as Role;
                          updateMutation.mutate(
                            { id: user.id, body: { role } },
                            {
                              onSuccess: () =>
                                toast(`${user.email} rolü güncellendi.`, "success"),
                              onError: (error) => toast(errorMessage(error), "error"),
                            },
                          );
                        }}
                        className="border-border bg-background text-foreground h-8 rounded-md border px-2 text-xs"
                      >
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </TD>
                    <TD>
                      {user.mfaEnabled ? (
                        <Badge tone="success">Açık</Badge>
                      ) : (
                        <Badge tone="muted">Kapalı</Badge>
                      )}
                    </TD>
                    <TD className="text-muted-foreground whitespace-nowrap text-xs">
                      {formatDateTime(user.createdAt)}
                    </TD>
                    <TD className="text-muted-foreground whitespace-nowrap text-xs">
                      {user.lastLoginAt ? formatRelative(user.lastLoginAt) : "—"}
                    </TD>
                    <TD>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={user.id === currentUser?.id}
                        onClick={() => setPendingDelete(user)}
                      >
                        Sil
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Yeni kullanıcı */}
      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        title="Yeni kullanıcı"
        description="Hesap oluşturulur ve verilen rolle giriş yapabilir."
      >
        {form ? (
          <form onSubmit={onCreate} className="flex flex-col gap-4">
            <Input
              label="E-posta"
              type="email"
              required
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
            <Input
              label="Ad soyad"
              value={form.displayName}
              onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            />
            <Input
              label="Parola"
              type="password"
              required
              hint="En az 12 karakter"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
            <Select
              label="Rol"
              options={roleOptions}
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
            />
            {formError ? (
              <p role="alert" className="bg-danger/10 text-danger rounded-md border border-danger/30 px-3 py-2 text-sm">
                {formError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setForm(null)}>
                Vazgeç
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Oluşturuluyor…" : "Oluştur"}
              </Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      {/* Silme onayı */}
      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Kullanıcı silinsin mi?"
        description={pendingDelete ? `${pendingDelete.email} hesabı kaldırılacak.` : undefined}
      >
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPendingDelete(null)}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (!pendingDelete) return;
              deleteMutation.mutate(pendingDelete.id, {
                onSuccess: () => {
                  toast("Kullanıcı silindi.", "success");
                  setPendingDelete(null);
                },
                onError: (error) => toast(errorMessage(error), "error"),
              });
            }}
          >
            Sil
          </Button>
        </div>
      </Dialog>
    </div>
  );
}